import { useCallback, useEffect, useState, type ChangeEvent } from "react";

export type UseNumberInputOptions = {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  /** 0 = integers only. Default 0. */
  maxDecimals?: number;
  /** Round to this increment on blur. */
  step?: number;
};

/** Display formatting: drop trailing zeros after the decimal. */
export function formatNumberInputValue(
  value: number,
  maxDecimals: number,
): string {
  if (!Number.isFinite(value)) return "";
  if (maxDecimals <= 0) return String(Math.round(value));
  const rounded = Number(value.toFixed(maxDecimals));
  return Number.isInteger(rounded) ? String(rounded) : String(rounded);
}

/** True when `draft` is empty or a valid in-progress number string. */
export function isAllowedNumberDraft(
  draft: string,
  maxDecimals: number,
): boolean {
  if (draft === "") return true;
  if (maxDecimals <= 0) return /^[0-9]+$/.test(draft);
  if (!/^[0-9]*\.?[0-9]*$/.test(draft)) return false;
  const dot = draft.indexOf(".");
  if (dot === -1) return true;
  return draft.length - dot - 1 <= maxDecimals;
}

/** Incomplete drafts (`""`, `"."`) are not a number yet. */
export function parseNumberDraft(draft: string): number | null {
  if (draft === "" || draft === ".") return null;
  const parsed = Number(draft);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Parse, round to step, and clamp for blur / commit. */
export function commitNumberValue(
  draft: string,
  options: {
    min?: number;
    max?: number;
    maxDecimals?: number;
    step?: number;
    fallback: number;
  },
): number {
  const maxDecimals = options.maxDecimals ?? 0;
  let next = parseNumberDraft(draft) ?? options.fallback;
  if (options.step != null && options.step > 0) {
    next = Math.round(next / options.step) * options.step;
  }
  next =
    maxDecimals <= 0 ? Math.round(next) : Number(next.toFixed(maxDecimals));
  if (options.min != null) next = Math.max(options.min, next);
  if (options.max != null) next = Math.min(options.max, next);
  return next;
}

/**
 * Controlled number typing: keep a draft string while focused so empty and
 * trailing-decimal states are allowed, then clamp and format on blur.
 */
export function useNumberInput({
  value,
  onChange,
  min,
  max,
  maxDecimals = 0,
  step,
}: UseNumberInputOptions) {
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState(() =>
    formatNumberInputValue(value, maxDecimals),
  );

  useEffect(() => {
    if (!focused) {
      setDraft(formatNumberInputValue(value, maxDecimals));
    }
  }, [value, focused, maxDecimals]);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const nextDraft = event.target.value;
      if (!isAllowedNumberDraft(nextDraft, maxDecimals)) return;
      setDraft(nextDraft);
      const parsed = parseNumberDraft(nextDraft);
      if (parsed === null) return;
      if (min != null && parsed < min) return;
      if (max != null && parsed > max) return;
      onChange(parsed);
    },
    [max, maxDecimals, min, onChange],
  );

  const handleFocus = useCallback(() => {
    setFocused(true);
  }, []);

  const handleBlur = useCallback(() => {
    const next = commitNumberValue(draft, {
      min,
      max,
      maxDecimals,
      step,
      fallback: min ?? value,
    });
    onChange(next);
    setDraft(formatNumberInputValue(next, maxDecimals));
    setFocused(false);
  }, [draft, max, maxDecimals, min, onChange, step, value]);

  return {
    value: focused ? draft : formatNumberInputValue(value, maxDecimals),
    onChange: handleChange,
    onFocus: handleFocus,
    onBlur: handleBlur,
    inputMode: (maxDecimals > 0 ? "decimal" : "numeric") as
      | "decimal"
      | "numeric",
  };
}
